import { GlideIcon, ErrorBoundary } from "@glide/common";
import { GlideJSON } from "@glide/data-types";
import type { WireDataPlotComponent } from "@glide/fluent-components/dist/js/base-components";
import {
    type TimeSeriesRange,
    isTimeSeriesRange,
    convertTimeDurationToBucket,
    getDefaultIntervalFromDuration,
} from "@glide/generator/dist/js/components/data-plot-advanced-chart/data-plot-time-series-helpers";
import type { WireBackendInterface } from "@glide/hydrated-ui";
import { ValueChangeSource, APP_MODAL_ROOT } from "@glide/wire";
import { mapFilterUndefined } from "@glideapps/ts-necessities";
import { definedMap } from "collection-utils";
import * as WireDropdown from "../../../renderers/wire-dropdown-menu/wire-dropdown-menu";
import { getWireValue } from "../../../wire-lib";
import * as React from "react";
import { getLocalizedString } from "@glide/localization";
import "twin.macro";
import { isSmallScreen, useIsHoverable, useRootResponsiveSizeClass } from "@glide/common-components";
import type { TimeSeriesRelativeDuration } from "@glide/component-utils";
import {
    localizeTimeSeriesRelativeDuration,
    TimeSeriesInterval,
    ALL_TIME_SERIES_INTERVAL,
} from "@glide/component-utils";

const RangePicker = React.lazy(() => import("./time-range-picker"));

interface PropoverProps extends React.PropsWithChildren {
    smallScreen: boolean;
}

const Popover: React.FC<PropoverProps> = ({ children, smallScreen }) => {
    return (
        <WireDropdown.Portal container={document.getElementById(APP_MODAL_ROOT)}>
            <WireDropdown.Content
                align="start"
                side="bottom"
                sideOffset={2}
                style={smallScreen ? { width: "calc(100vw - 2rem)", margin: "0 1rem" } : undefined}
                tw="min-w-[200px]">
                {children}
            </WireDropdown.Content>
        </WireDropdown.Portal>
    );
};

export const TimeSeriesSelector: React.FC<{
    timeSeriesRelativeDurationState: WireDataPlotComponent["timeSeriesRelativeDurationState"];
    timeSeriesIntervalState: WireDataPlotComponent["timeSeriesIntervalState"];
    timeSeriesEffectiveInterval: WireDataPlotComponent["timeSeriesEffectiveInterval"];
    backend: WireBackendInterface;
    timeRangeOptions: WireDataPlotComponent["timeRangeOptions"];
}> = p => {
    const {
        backend,
        timeSeriesRelativeDurationState,
        timeSeriesIntervalState: intervalState,
        timeSeriesEffectiveInterval,
        timeRangeOptions,
    } = p;
    const [showCustomRangePicker, setShowCustomRangePicker] = React.useState(false);
    const timeSeriesState = definedMap(timeSeriesRelativeDurationState, state => getWireValue(state, backend));
    const timeSeries = timeSeriesState?.[0];
    const setTimeSeries = timeSeriesState?.[1];

    const timeSeriesIntervalState = definedMap(intervalState, state => getWireValue(state, backend));
    const timeSeriesInterval = timeSeriesIntervalState?.[0] ?? timeSeriesEffectiveInterval;
    const setTimeSeriesInterval = timeSeriesIntervalState?.[1];

    const isTouch = !useIsHoverable();
    const sizeClass = useRootResponsiveSizeClass();
    const smallScreen = isSmallScreen(sizeClass);
    // Samsung phones matches against the media query we use for changing hover capabilities
    // so we need to also consider the screenSize in this case
    const isMobile = isTouch || smallScreen;

    const onChangeTimeSeries = React.useCallback(
        (newTimeSerie: TimeSeriesRelativeDuration | "All") => {
            if (timeSeries === newTimeSerie) {
                return;
            }
            setTimeSeries?.(newTimeSerie, ValueChangeSource.User);
            const newInterval = getDefaultIntervalFromDuration(newTimeSerie);
            if (newInterval !== timeSeriesInterval) {
                setTimeSeriesInterval?.(newInterval, ValueChangeSource.User);
            }
        },
        [timeSeries, setTimeSeries, timeSeriesInterval, setTimeSeriesInterval]
    );
    const onChangeCustomRange = React.useCallback(
        (newTimeSerie: TimeSeriesRange) => {
            if (isTimeSeriesRange(newTimeSerie)) {
                const json = GlideJSON.fromJSON(newTimeSerie);
                setTimeSeries?.(json, ValueChangeSource.User);
                const newInterval = getDefaultIntervalFromDuration(json);
                if (newInterval !== timeSeriesInterval) {
                    setTimeSeriesInterval?.(newInterval, ValueChangeSource.User);
                }
            }
        },
        [setTimeSeries, setTimeSeriesInterval, timeSeriesInterval]
    );
    const checkIfSelected = React.useCallback(
        (state: TimeSeriesRelativeDuration | "All") => {
            return state === timeSeries;
        },
        [timeSeries]
    );
    const timeSerieItems = React.useMemo(() => {
        const hasCustomOption = timeRangeOptions.includes("Custom");
        const presetRangeOptions = timeRangeOptions.filter(option => option !== "Custom"); // custom option will be added later if needed
        const displayableOptions = mapFilterUndefined<
            TimeSeriesRelativeDuration | "All" | "Custom",
            {
                isSelected: boolean;
                onSelect: () => void;
                value: TimeSeriesRelativeDuration | "All" | "Custom";
                displayValue: string;
            }
        >(presetRangeOptions, option => {
            if (option === "Custom") {
                return undefined;
            }
            return {
                isSelected: checkIfSelected(option),
                onSelect: () => onChangeTimeSeries(option),
                value: option,
                displayValue: option === "All" ? getLocalizedString("all") : localizeTimeSeriesRelativeDuration(option),
            };
        });

        if (hasCustomOption) {
            displayableOptions.push({
                isSelected:
                    showCustomRangePicker ||
                    (timeSeries instanceof GlideJSON && isTimeSeriesRange(timeSeries.jsonValue)),
                onSelect: () => setShowCustomRangePicker(true),
                value: "Custom",
                displayValue: `${getLocalizedString("custom")}…`,
            });
        }

        return displayableOptions;
    }, [checkIfSelected, onChangeTimeSeries, showCustomRangePicker, timeRangeOptions, timeSeries]);

    const unitItems = React.useMemo(
        () =>
            ALL_TIME_SERIES_INTERVAL.map(o => ({
                isSelected: timeSeriesInterval === o,
                onSelect: () => setTimeSeriesInterval?.(o, ValueChangeSource.User),
                value: o,
                displayValue: getLocalizedString(o),
            })),
        [timeSeriesInterval, setTimeSeriesInterval]
    );

    const selected = React.useMemo(() => timeSerieItems.filter(item => item.isSelected)[0], [timeSerieItems]);
    const selectedShortTitle = React.useMemo(() => {
        if (selected === undefined) return "All";

        if (selected.value === "Custom") {
            if (timeSeries instanceof GlideJSON && isTimeSeriesRange(timeSeries.jsonValue)) {
                return convertTimeDurationToBucket(timeSeries).name;
            }
        }

        return selected.displayValue?.replace("Past", "").trim() ?? "All";
    }, [selected, timeSeries]);
    return (
        <>
            <WireDropdown.Root>
                <WireDropdown.Trigger
                    tw="relative bg-n200A! border border-transparent flex shrink-0 grow-0 text-base text-text-contextual-xpale
                items-center justify-between h-9 p-2 transition duration-75 rounded-lg first:ml-0 not-last:mr-2 gp-md:(px-3 min-w-[150px])
                page-hover:(ring-1 ring-border-dark border-border-dark) focus-within:(ring-1 border-text-contextual-accent ring-text-contextual-accent)"
                    title={selectedShortTitle}
                    onClick={e => e.stopPropagation()}>
                    <div data-icon tw="transition-colors gp-md:mr-1.5">
                        <GlideIcon tw="-mb-px shrink-0" kind="stroke" icon="st-calendar" iconSize={20} />
                    </div>
                    <div tw="overflow-hidden flex-1 text-left whitespace-nowrap text-ellipsis hidden gp-md:block">
                        {selectedShortTitle}
                    </div>
                    <div data-icon tw="transition-colors ml-2 hidden gp-md:block">
                        <GlideIcon tw="-mb-px shrink-0" kind="stroke" icon="st-caret" iconSize={16} />
                    </div>
                </WireDropdown.Trigger>
                <Popover smallScreen={smallScreen}>
                    {timeSerieItems?.map(o => {
                        const isSelected = o.isSelected;
                        return (
                            <WireDropdown.CheckboxItem
                                key={o.value}
                                checked={isSelected}
                                onClick={e => e.stopPropagation()}
                                tw="p-1 w-52"
                                onSelect={o.onSelect}>
                                {o?.displayValue ?? "None"}
                            </WireDropdown.CheckboxItem>
                        );
                    })}
                    {isMobile ? null : (
                        <>
                            <div tw="border-b border-border-base my-1" />
                            <WireDropdown.Sub>
                                <WireDropdown.SubTrigger
                                    tw="outline-none rounded cursor-pointer leading-5 w-full text-sm font-normal transition-colors px-1 py-1.5 not-last:mb-px select-none flex items-center gap-2 data-[disabled]:pointer-events-none data-[disabled]:bg-none hocus:bg-n100A"
                                    onClick={e => e.stopPropagation()}>
                                    <span tw="grow shrink-0">Unit</span>
                                    <span tw="text-xs text-text-pale truncate">
                                        {getLocalizedString(timeSeriesInterval ?? TimeSeriesInterval.Day)}
                                    </span>
                                    <GlideIcon
                                        tw="text-icon-pale shrink-0"
                                        kind="stroke"
                                        icon="st-chevron-right"
                                        iconSize={16}
                                    />
                                </WireDropdown.SubTrigger>
                                <WireDropdown.Portal container={document.getElementById(APP_MODAL_ROOT)}>
                                    <WireDropdown.SubContent tw="min-w-[200px]">
                                        {unitItems.map(o => {
                                            const isSelected = o.isSelected;
                                            return (
                                                <WireDropdown.CheckboxItem
                                                    key={o.value}
                                                    checked={isSelected}
                                                    onClick={e => e.stopPropagation()}
                                                    tw="p-1 w-52"
                                                    onSelect={o.onSelect}>
                                                    {o?.displayValue ?? "None"}
                                                </WireDropdown.CheckboxItem>
                                            );
                                        })}
                                    </WireDropdown.SubContent>
                                </WireDropdown.Portal>
                            </WireDropdown.Sub>
                        </>
                    )}
                </Popover>
            </WireDropdown.Root>
            {isMobile ? (
                <WireDropdown.Root>
                    <WireDropdown.Trigger
                        tw="relative bg-n200A! border border-transparent flex shrink-0 grow-0 text-base text-text-contextual-xpale
                    items-center justify-between h-9 p-2 transition duration-75 rounded-lg first:ml-0 not-last:mr-2
                    page-hover:(ring-1 ring-border-dark border-border-dark) focus-within:(ring-1 border-text-contextual-accent ring-text-contextual-accent)
                    gp-md:hidden
                    "
                        title="Unit"
                        onClick={e => e.stopPropagation()}>
                        <div data-icon tw="transition-colors">
                            <GlideIcon tw="-mb-px shrink-0" kind="stroke" icon="st-ruler" iconSize={20} />
                        </div>
                    </WireDropdown.Trigger>
                    <Popover smallScreen={smallScreen}>
                        {unitItems?.map(o => {
                            const isSelected = o.isSelected;
                            return (
                                <WireDropdown.CheckboxItem
                                    key={o.value}
                                    checked={isSelected}
                                    onClick={e => e.stopPropagation()}
                                    tw="p-1 w-full"
                                    onSelect={o.onSelect}>
                                    {o?.displayValue ?? "None"}
                                </WireDropdown.CheckboxItem>
                            );
                        })}
                    </Popover>
                </WireDropdown.Root>
            ) : null}
            {showCustomRangePicker && (
                <ErrorBoundary>
                    <React.Suspense fallback={<></>}>
                        <RangePicker
                            value={
                                timeSeries instanceof GlideJSON && isTimeSeriesRange(timeSeries.jsonValue)
                                    ? timeSeries.jsonValue
                                    : undefined
                            }
                            onUpdate={onChangeCustomRange}
                            onClose={() => setShowCustomRangePicker(false)}
                        />
                    </React.Suspense>
                </ErrorBoundary>
            )}
        </>
    );
};
